import type { StoragePort } from "../ports.ts";
import {
  clearRecentLogs,
  setChannelAllowlist,
  setDevMinimumLogLevel,
} from "./gameLogger.ts";
import {
  LOG_CHANNELS,
  LOG_LEVELS,
  type LogChannel,
  type LogLevel,
} from "./types.ts";

const KNOWN_CHANNELS: ReadonlySet<string> = new Set(LOG_CHANNELS);
const KNOWN_LEVELS: ReadonlySet<string> = new Set(LOG_LEVELS);

function isLogChannel(value: string): value is LogChannel {
  return KNOWN_CHANNELS.has(value);
}

function isLogLevel(value: string): value is LogLevel {
  return KNOWN_LEVELS.has(value);
}

export function parseLogChannelsParam(
  raw: string | null | undefined,
): LogChannel[] | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }

  const channels = trimmed
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .filter(isLogChannel);

  if (channels.length === 0) {
    return null;
  }

  return channels;
}

export function parseLogLevelParam(
  raw: string | null | undefined,
): LogLevel | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  const normalized = raw.trim().toLowerCase();
  if (normalized === "" || !isLogLevel(normalized)) {
    return null;
  }

  return normalized;
}

export const LOG_CHANNELS_STORAGE_KEY = "dawn.logChannels";
export const LOG_LEVEL_STORAGE_KEY = "dawn.logLevel";

export interface LogPreferences {
  channels: LogChannel[] | null;
  level: LogLevel | null;
}

/**
 * Resolve channel/level filters for DEV logging.
 *
 * Filter syntax (maintainer-facing):
 * - `?log=session,economy` — comma-separated channel allowlist
 * - `?level=debug|info|warn|error|trace` — minimum level
 * - Persisted as `dawn.logChannels` / `dawn.logLevel` in localStorage
 * - Clear: `localStorage.removeItem(...)` or `?log=` / invalid `?level=` when the param is present
 *
 * URL wins when the param is present; otherwise storage. Unset means all channels /
 * debug-mode level default. Filtered entries are omitted from console and the ring buffer.
 */
export function resolveLogPreferences(
  search: string,
  storage: StoragePort,
): LogPreferences {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );

  let channels: LogChannel[] | null;
  if (params.has("log")) {
    channels = parseLogChannelsParam(params.get("log"));
    if (channels === null) {
      storage.removeItem(LOG_CHANNELS_STORAGE_KEY);
    } else {
      storage.setItem(LOG_CHANNELS_STORAGE_KEY, channels.join(","));
    }
  } else {
    channels = parseLogChannelsParam(storage.getItem(LOG_CHANNELS_STORAGE_KEY));
  }

  let level: LogLevel | null;
  if (params.has("level")) {
    level = parseLogLevelParam(params.get("level"));
    if (level === null) {
      storage.removeItem(LOG_LEVEL_STORAGE_KEY);
    } else {
      storage.setItem(LOG_LEVEL_STORAGE_KEY, level);
    }
  } else {
    level = parseLogLevelParam(storage.getItem(LOG_LEVEL_STORAGE_KEY));
  }

  return { channels, level };
}

export function hasExplicitLogLevelPreference(storage: StoragePort): boolean {
  return parseLogLevelParam(storage.getItem(LOG_LEVEL_STORAGE_KEY)) !== null;
}

export function applyLogPreferences(
  prefs: LogPreferences,
  fallbackMinLevel: LogLevel,
): void {
  setChannelAllowlist(prefs.channels);
  setDevMinimumLogLevel(prefs.level ?? fallbackMinLevel);
}

export function clearAppliedLogPreferences(
  fallbackMinLevel: LogLevel = "info",
): void {
  setChannelAllowlist(null);
  clearRecentLogs();
  setDevMinimumLogLevel(fallbackMinLevel);
}
